/* ==========================================================================
   a11y-dialog.js — minimal modal-dialog accessibility helper (WCAG 2.1 AA)
   PR 3 (Modals/Drawers/Navigation ARIA).

   Vanilla JS, no dependencies. Coexists with the theme's jQuery code: the
   component still does its own visual open/close (class toggling); this
   helper only adds the keyboard/focus behaviour a dialog needs:

     - focus trap   — Tab / Shift+Tab cycle within the dialog (WCAG 2.4.3)
     - ESC to close — calls the supplied onClose callback (WCAG 2.1.2)
     - initial focus — moves focus into the dialog on open
     - focus restore — returns focus to the trigger on close

   Usage:
     A11yDialog.open(panelEl, { onClose: closeFn });  // after showing it
     A11yDialog.close(panelEl);                        // after hiding it
   ========================================================================== */
window.A11yDialog = (function () {
  var FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  // Only one dialog is trapped at a time.
  var active = null; // { panel, prevFocus, onClose, keyHandler }

  function focusableItems(panel) {
    return Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      function (el) {
        // skip elements that are not rendered
        return (
          el.offsetWidth > 0 ||
          el.offsetHeight > 0 ||
          el === document.activeElement
        );
      },
    );
  }

  function onKeydown(e) {
    if (!active) return;
    var key = e.key;

    if (key === "Escape" || key === "Esc") {
      e.preventDefault();
      if (typeof active.onClose === "function") active.onClose();
      else close(active.panel);
      return;
    }

    if (key !== "Tab") return;

    var items = focusableItems(active.panel);
    if (!items.length) {
      // nothing focusable inside — keep focus on the panel itself
      e.preventDefault();
      active.panel.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];

    if (!active.panel.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function open(panel, opts) {
    if (!panel) return;
    opts = opts || {};

    // If another dialog is open, release it first.
    if (active && active.panel !== panel) close(active.panel);

    var prevFocus = document.activeElement;
    document.addEventListener("keydown", onKeydown, true);
    active = {
      panel: panel,
      prevFocus: prevFocus,
      onClose: opts.onClose,
      keyHandler: onKeydown,
    };

    // Defer so a CSS open-transition / display change has applied before we
    // try to focus (a display:none element cannot receive focus).
    setTimeout(function () {
      if (!active || active.panel !== panel) return;
      var items = focusableItems(panel);
      var target = opts.initialFocus || items[0] || panel;
      try {
        target.focus();
      } catch (err) {
        /* element not focusable — ignore */
      }
    }, 60);
  }

  function close(panel) {
    if (!active) return;
    if (panel && active.panel !== panel) return;

    document.removeEventListener("keydown", active.keyHandler, true);
    var prev = active.prevFocus;
    active = null;

    if (prev && typeof prev.focus === "function") {
      try {
        prev.focus();
      } catch (err) {
        /* trigger gone from the DOM — ignore */
      }
    }
  }

  return { open: open, close: close };
})();
